import { Button } from '@/components/ui/button';
import { CheckCircle, Filter, ShieldCheck } from 'lucide-react';

interface EmptyStateProps {
  hasFilters: boolean;
  onClearFilters: () => void;
}

export function EmptyState({ hasFilters, onClearFilters }: Readonly<EmptyStateProps>) {
  if (hasFilters) {
    // No results from filters
    return (
      <div className="text-center py-20 border border-border rounded-lg bg-card shadow-sm">
        <div className="flex justify-center mb-6">
          <div className="p-5 bg-muted rounded-full">
            <Filter className="h-14 w-14 text-muted-foreground" />
          </div>
        </div>
        <h3 className="text-2xl font-semibold mb-3">No Matching Exceptions</h3>
        <p className="text-muted-foreground mb-8 max-w-md mx-auto leading-relaxed">
          We couldn't find any exceptions matching your current filters. Try adjusting your search criteria or clearing filters to see all exceptions.
        </p>
        <Button onClick={onClearFilters} variant="outline" size="lg" className="px-6">
          <Filter className="h-4 w-4 mr-2" />
          Clear All Filters
        </Button>
      </div>
    );
  }

  // No exceptions at all - world class design
  return (
    <div className="text-center py-24 border border-border rounded-lg bg-gradient-to-br from-green-50/50 to-emerald-50/30 dark:from-green-950/20 dark:to-emerald-950/10 shadow-sm">
      <div className="flex justify-center mb-6">
        <div className="relative">
          <div className="absolute inset-0 bg-green-500/20 rounded-full blur-xl animate-pulse"></div>
          <div className="relative p-6 bg-gradient-to-br from-green-100 to-emerald-100 dark:from-green-900/50 dark:to-emerald-900/50 rounded-full shadow-lg">
            <ShieldCheck className="h-16 w-16 text-green-600 dark:text-green-400" />
          </div>
        </div>
      </div>
      <h3 className="text-2xl font-bold mb-3 text-green-900 dark:text-green-100">All Clear!</h3>
      <p className="text-lg text-green-700 dark:text-green-300 mb-2 font-medium">No Exceptions Found</p>
      <p className="text-muted-foreground max-w-md mx-auto leading-relaxed">
        Excellent work! All assets match their records perfectly. There are no exceptions to review at this time.
      </p>
      <div className="mt-8 flex items-center justify-center gap-2 text-sm text-muted-foreground">
        <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
        <span>Your asset audit is in perfect order</span>
      </div>
    </div>
  );
}
